import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Application, Project, UserGroup, UserConfig } from '../types/config';
import { getConfig } from '../config';
import { loadConfigScripts, clearScriptState } from '../services/scriptHelper';

const STORAGE_KEY = 'dt_platform_app_state';

interface StoredState {
  applicationId?: string;
  projectId?: string;
  userGroupId?: string;
}

interface AppContextValue {
  // Selected entities
  selectedApplication: Application | null;
  selectedProject: Project | null;
  selectedUserGroup: UserGroup | null;
  userConfig: UserConfig | null;

  // Namespaces for API calls
  applicationNamespaces: string[];
  projectNamespaces: string[];
  userGroupNamespaces: string[];
  allNamespaces: string[];

  // Script loading state
  isLoadingScripts: boolean;

  // Actions
  setSelectedApplication: (app: Application | null) => void;
  setSelectedProject: (project: Project | null) => void;
  setSelectedUserGroup: (group: UserGroup | null) => void;
  setUserConfig: (config: UserConfig | null) => Promise<void>;
  clearSelection: () => void;

  // Stored IDs (for restoration)
  storedState: StoredState;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
  initialApplication?: Application | null;
  baseNamespaces?: string[];
}

export function AppProvider({ children, initialApplication, baseNamespaces = [] }: AppProviderProps) {
  const [selectedApplication, setSelectedApplicationState] = useState<Application | null>(initialApplication || null);
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [selectedUserGroup, setSelectedUserGroupState] = useState<UserGroup | null>(null);
  const [userConfig, setUserConfigState] = useState<UserConfig | null>(null);
  const [storedState, setStoredState] = useState<StoredState>({});
  const [isLoadingScripts, setIsLoadingScripts] = useState(false);

  // Sync with initialApplication when it changes (from AuthContext)
  useEffect(() => {
    if (initialApplication && !selectedApplication) {
      setSelectedApplicationState(initialApplication);
    }
  }, [initialApplication]);

  // Load stored state on mount
  useEffect(() => {
    const config = getConfig();
    const stored = localStorage.getItem(STORAGE_KEY);
    let state: StoredState = {};

    if (stored) {
      try {
        state = JSON.parse(stored);
      } catch {
        // Invalid stored data
      }
    }

    // Override with config values if present
    if (config.applicationId) state.applicationId = config.applicationId;
    if (config.projectId) state.projectId = config.projectId;
    if (config.userGroupId) state.userGroupId = config.userGroupId;

    setStoredState(state);
  }, []);

  // Save state changes
  const saveState = useCallback((update: Partial<StoredState>) => {
    setStoredState(prev => {
      const newState = { ...prev, ...update };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      return newState;
    });
  }, []);

  // Computed namespaces - use baseNamespaces from AuthContext as fallback
  const applicationNamespaces = selectedApplication?._namespaces || baseNamespaces;
  const projectNamespaces = selectedProject?._namespaces || [];
  const userGroupNamespaces = selectedUserGroup?._namespaces || [];

  // Combine all namespaces for API calls (most specific first)
  const allNamespaces = [
    ...userGroupNamespaces,
    ...projectNamespaces,
    ...applicationNamespaces,
  ].filter((ns, index, self) => self.indexOf(ns) === index); // Remove duplicates

  const setSelectedApplication = useCallback((app: Application | null) => {
    setSelectedApplicationState(app);
    if (app) {
      saveState({ applicationId: app._id });
    }
  }, [saveState]);

  const setSelectedProject = useCallback((project: Project | null) => {
    setSelectedProjectState(project);
    if (project) {
      saveState({ projectId: project._id });
      // Store project in sessionStorage for ipa-core components
      sessionStorage.setItem('project', JSON.stringify(project));
    } else {
      sessionStorage.removeItem('project');
    }
  }, [saveState]);

  const setSelectedUserGroup = useCallback((group: UserGroup | null) => {
    setSelectedUserGroupState(group);
    if (group) {
      saveState({ userGroupId: group._id });
    }
  }, [saveState]);

  const setUserConfig = useCallback(async (config: UserConfig | null) => {
    setUserConfigState(config);

    if (config) {
      setIsLoadingScripts(true);
      try {
        // Clear any previous script state
        clearScriptState();

        // Load and execute config-level scripts
        // ScriptHelper uses IafSession internally, so no need to pass context
        await loadConfigScripts(config);

        console.log('Config scripts loaded successfully');
      } catch (error) {
        console.error('Failed to load config scripts:', error);
        // Don't throw - allow the config to be set even if scripts fail
      } finally {
        setIsLoadingScripts(false);
      }
    } else {
      setIsLoadingScripts(false);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedApplicationState(null);
    setSelectedProjectState(null);
    setSelectedUserGroupState(null);
    setUserConfigState(null);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('project');
    clearScriptState();
    setStoredState({});
  }, []);

  const value: AppContextValue = {
    selectedApplication,
    selectedProject,
    selectedUserGroup,
    userConfig,
    applicationNamespaces,
    projectNamespaces,
    userGroupNamespaces,
    allNamespaces,
    isLoadingScripts,
    setSelectedApplication,
    setSelectedProject,
    setSelectedUserGroup,
    setUserConfig,
    clearSelection,
    storedState,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
